/**
 * client/src/lib/idb.ts — the browser's own storage, in one file.
 *
 * Three things live here, and they are three because they have three different lifetimes:
 *
 *  1. **`kv`** — the read-through cache behind `useCachedQuery`. Painted first, overwritten by the
 *     network, never authoritative.
 *  2. **`outbox`** — writes made while offline, waiting to be sent (`client/src/lib/outbox.ts` owns
 *     the semantics; this file owns the bytes).
 *  3. **`offline`** — the download manifest: which books this browser has pulled, which page
 *     artifacts it holds for each, and how many bytes those are. It is the index over the
 *     `ab-audio-v1` Cache, and it is what the offline shelf is built from.
 *
 * Why 1 exists in P1, when offline books are P6: the primary device is an iPhone in bed, and the
 * first paint of the shelf on a slow or dropped connection is otherwise a spinner over an empty
 * page. Painting the last known shelf immediately and revalidating behind it is the difference
 * between "it's loading" and "it's broken" — and it is the same stale-while-revalidate discipline
 * §5.8's `ab-book-v1` cache uses, built once here rather than twice.
 *
 * Two rules this file exists to hold:
 *
 *  1. **`kv` is a cache, never a source of truth.** Nothing is read from there that has not also been
 *     asked for over the network in the same breath, and a cached row never outranks a fresh one.
 *     Reading positions do NOT live in `kv` — `positions` is a server table with last-write-wins over
 *     `clientUpdatedAt` (`shared/position.ts`), and a second local copy is a second answer to
 *     "where am I", which is exactly the class of bug this repo is organised against. (The `outbox`
 *     store is not a second answer: it holds writes that have not landed *yet*, each stamped with the
 *     same `clientUpdatedAt` the server will resolve them by.)
 *  2. **Unavailable storage is a supported state.** Safari in private mode throws on `indexedDB.open`
 *     (and Lockdown Mode can refuse it outright). Everything degrades to a per-tab `Map`, silently,
 *     because a reading app that fails to open because it could not open a *cache* is absurd.
 *
 * ── The one CacheStorage bucket the page owns ────────────────────────────────────────────────────
 *
 * `ab-audio-v1` is at the bottom of this file rather than in `client/src/sw.ts`, and that is **LAW
 * 14**: the service worker must not claim `/api/v1/audio/`, so it cannot be the thing that fills or
 * reads the audio cache either. A downloaded page's bytes are put there by `DownloadBook` and taken
 * back out by the audio engine's host `fetch`, both in page scope, both producing a Blob — which is
 * §5.7's stated source strategy for an offline book anyway (`fetch → Blob → objectURL`). The service
 * worker names the cache in exactly one place, its `activate` sweep, so that it never deletes it;
 * `test/sw.test.ts` asserts the two names agree.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const DB_NAME = "audiobook";

/**
 * v1 was `kv` alone. v2 adds `outbox` and `offline`.
 *
 * The upgrade is additive and `onupgradeneeded` creates only what is missing, so a browser that has
 * been reading since P1 keeps its cached shelf. A migration that dropped and recreated `kv` would
 * cost nothing in correctness and would blank the first paint of the shelf for every existing
 * reader, which is the one thing that store exists to prevent.
 */
const DB_VERSION = 2;

const KV = "kv";
const OUTBOX = "outbox";
const OFFLINE = "offline";

interface Entry {
  key: string;
  value: unknown;
  /** ms epoch of the write. Callers decide what "too old to paint" means; this file has no opinion. */
  at: number;
}

/**
 * The fallback when IndexedDB is unavailable. Per-tab, lost on reload, and that is fine for `kv`.
 *
 * It is deliberately fine for `outbox` too: a queued position whose only copy was in memory is lost
 * when the tab goes, and the position was already mirrored to `localStorage` by `PositionWriter`. A
 * reader in Safari private mode does not get durable offline queueing, and pretending otherwise
 * would be worse than the honest degradation.
 */
const memory = new Map<string, Map<string, Entry>>();
const mem = (store: string): Map<string, Entry> => {
  let m = memory.get(store);
  if (!m) memory.set(store, (m = new Map()));
  return m;
};

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV, { keyPath: "key" });
      if (!db.objectStoreNames.contains(OUTBOX)) {
        // `at` is indexed because the outbox is drained in the order things happened. Two reports
        // filed in a tunnel must reach the server in the order they were written, and a `getAll()`
        // over a keyPath that happens to sort by a random id would deliver them backwards.
        db.createObjectStore(OUTBOX, { keyPath: "key" }).createIndex("at", "at");
      }
      if (!db.objectStoreNames.contains(OFFLINE)) db.createObjectStore(OFFLINE, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    // A blocked upgrade means another tab holds an older version open. Don't hang the boot on it.
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function txOn<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db || !db.objectStoreNames.contains(store)) {
          resolve(null);
          return;
        }
        let request: IDBRequest;
        try {
          request = run(db.transaction(store, mode).objectStore(store));
        } catch {
          resolve(null);
          return;
        }
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => resolve(null);
      }),
  );
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<T | null> {
  return txOn<T>(KV, mode, run);
}

// ─── the kv store ────────────────────────────────────────────────────────────

export async function idbGet<T>(key: string): Promise<{ value: T; at: number } | null> {
  const hit = mem(KV).get(key);
  if (hit) return { value: hit.value as T, at: hit.at };
  const row = await tx<Entry>("readonly", (s) => s.get(key));
  return row ? { value: row.value as T, at: row.at } : null;
}

export async function idbSet(key: string, value: unknown): Promise<void> {
  const entry: Entry = { key, value, at: Date.now() };
  mem(KV).set(key, entry);
  await tx("readwrite", (s) => s.put(entry));
}

export async function idbDel(key: string): Promise<void> {
  mem(KV).delete(key);
  await tx("readwrite", (s) => s.delete(key));
}

/** Every key we hold, for a "clear cached books" control and for the offline manifest to walk. */
export async function idbKeys(prefix = ""): Promise<string[]> {
  const keys = (await tx<IDBValidKey[]>("readonly", (s) => s.getAllKeys())) ?? [];
  const all = new Set<string>([...mem(KV).keys(), ...keys.map(String)]);
  return [...all].filter((k) => k.startsWith(prefix)).sort();
}

// ─── the outbox store ────────────────────────────────────────────────────────
//
// Rows in, rows out, oldest first. What a row MEANS — how a position coalesces, when a failure is
// permanent — is `client/src/lib/outbox.ts`. This half only knows keys, values and order.

/** One queued write. `key` is the coalescing identity; two rows with one key are one write. */
export interface OutboxRow {
  key: string;
  /** ms epoch when the write was first attempted. Drain order, and the age shown to the reader. */
  at: number;
  value: unknown;
}

export async function outboxPut(row: OutboxRow): Promise<void> {
  mem(OUTBOX).set(row.key, { key: row.key, value: row.value, at: row.at });
  await txOn(OUTBOX, "readwrite", (s) => s.put(row));
}

export async function outboxAll(): Promise<OutboxRow[]> {
  const rows = (await txOn<OutboxRow[]>(OUTBOX, "readonly", (s) => s.getAll())) ?? [];
  const byKey = new Map<string, OutboxRow>();
  for (const [key, entry] of mem(OUTBOX)) byKey.set(key, { key, at: entry.at, value: entry.value });
  // Disk first, memory second: the memory map is the same rows when IndexedDB works, and the ONLY
  // rows when it does not. Later writes win, and the memory copy is always the later one.
  for (const row of rows) if (!byKey.has(row.key)) byKey.set(row.key, row);
  return [...byKey.values()].sort((a, b) => a.at - b.at || (a.key < b.key ? -1 : 1));
}

export async function outboxDel(key: string): Promise<void> {
  mem(OUTBOX).delete(key);
  await txOn(OUTBOX, "readwrite", (s) => s.delete(key));
}

// ─── the offline manifest ────────────────────────────────────────────────────

/** One page artifact this browser is holding bytes for, and where those bytes are addressed. */
export interface OfflinePage {
  pageId: string;
  /** `/api/v1/audio/page/c003.p07.m4a?v=<sha>` — the Cache key, and a content hash (LAW 18's `?v=`). */
  url: string;
  bytes: number;
}

/**
 * A downloaded book, as this browser knows it.
 *
 * ⚠️ **`editionId` is on the record and it is LAW 1, not bookkeeping.** A sid is only meaningful
 * inside its edition; a book that has been re-imported since the download has a different current
 * edition, every cached page id addresses the old one, and the honest thing to say is "this download
 * is for an older version of the book" rather than to serve a chapter whose sentences have moved.
 */
export interface OfflineBook {
  bookId: string;
  editionId: string;
  title: string;
  author: string | null;
  coverSha: string | null;
  pages: OfflinePage[];
  /** Sum of `pages[].bytes` — measured, never estimated, because it is stated to a person. */
  bytes: number;
  /** How many pages the book has in total, so "38 of 104 pages have a voice" is sayable. */
  pagesTotal: number;
  /** When the download finished. */
  at: number;
  /**
   * When the download was last verified still present.
   *
   * §5.8: **Safari evicts script-writable storage after 7 days without a visit unless the site is on
   * the home screen.** That is why the label everywhere says *"available offline (recently)"* and
   * never "downloaded" — this timestamp is the honest thing behind that word.
   */
  checkedAt: number;
  /**
   * Whether the world could have read this book anyway.
   *
   * ⚠️ **This product is for a device several people share, and `CacheStorage` survives sign-out.**
   * `sw.ts`'s own header names that hazard and then caches book content anyway — deliberately, and
   * documented, because reading offline is the whole feature. What was missing is the other end:
   * `signOut()` cleared **nothing**, and the `ab-clear-book-cache` message the worker implements had
   * no sender anywhere in the client.
   *
   * The walk: A signs in, opens a private book — the worker stores `/books/:id`, `/chapters` and
   * `/chapters/:ord` in `ab-book-v1` despite the server's `no-store` — downloads it, signs out. B
   * opens the app with the radio off, lands on `/offline` (deliberately un-`<Gate>`d), and sees the
   * title, the cover and a link straight into the text and the audio.
   *
   * `public` is the one value that makes keeping it correct: a public book is one anybody could have
   * opened without signing in at all, so dropping it on sign-out would delete a stranger's download
   * to protect nothing. Anything else — and anything from before this field existed, which reads as
   * `undefined` — is treated as private and dropped. The cost of that default is a re-download; the
   * cost of the other default is the failure above.
   */
  visibility?: "private" | "unlisted" | "public";
}

const offlineKey = (bookId: string) => `ob:${bookId}`;

export async function putOfflineBook(book: OfflineBook): Promise<void> {
  const row = { key: offlineKey(book.bookId), at: book.at, value: book };
  mem(OFFLINE).set(row.key, { key: row.key, value: book, at: book.at });
  await txOn(OFFLINE, "readwrite", (s) => s.put(row));
}

export async function getOfflineBook(bookId: string): Promise<OfflineBook | null> {
  const key = offlineKey(bookId);
  const hit = mem(OFFLINE).get(key);
  if (hit) return hit.value as OfflineBook;
  const row = await txOn<{ key: string; value: OfflineBook }>(OFFLINE, "readonly", (s) => s.get(key));
  return row?.value ?? null;
}

export async function listOfflineBooks(): Promise<OfflineBook[]> {
  const rows = (await txOn<{ key: string; value: OfflineBook }[]>(OFFLINE, "readonly", (s) => s.getAll())) ?? [];
  const byId = new Map<string, OfflineBook>();
  for (const row of rows) if (row?.value?.bookId) byId.set(row.value.bookId, row.value);
  for (const entry of mem(OFFLINE).values()) {
    const b = entry.value as OfflineBook;
    if (b?.bookId) byId.set(b.bookId, b);
  }
  return [...byId.values()].sort((a, b) => b.at - a.at);
}

/** Forget a book AND give its bytes back. The manifest without the eviction is a lie about storage. */
export async function removeOfflineBook(bookId: string): Promise<number> {
  const book = await getOfflineBook(bookId);
  const key = offlineKey(bookId);
  mem(OFFLINE).delete(key);
  await txOn(OFFLINE, "readwrite", (s) => s.delete(key));
  if (!book) return 0;

  // Another downloaded book can legitimately hold the same artifact URL: pages are addressed by page
  // id, but two copies of the same public-domain text produce the same clips, the same artifact sha
  // and — as P4's gate observed live — the same `page_audio.artifactKey`. Deleting this book's URLs
  // blindly would silently un-download the other one.
  const stillWanted = new Set<string>();
  for (const other of await listOfflineBooks()) {
    for (const page of other.pages) stillWanted.add(page.url);
  }
  const cache = await openAudioCache();
  if (!cache) return 0;
  let freed = 0;
  for (const page of book.pages) {
    if (stillWanted.has(page.url)) continue;
    if (await cache.delete(page.url)) freed += page.bytes;
  }
  return freed;
}

/**
 * Sign-out, on the storage side. Called by `signOut()` in `client/src/lib/auth.tsx`.
 *
 * ⚠️ **Nothing called this before, and the worker's half of it had no sender at all.** `sw.ts`
 * implements `ab-clear-book-cache`; grepping the client found the message posted from nowhere —
 * only `ab-skip-waiting` was ever sent. `signOut()` cleared no cache, no manifest and no audio, and
 * `/offline` is deliberately un-`<Gate>`d, so the next person to pick up the phone got the previous
 * person's private library, offline, with a working link into it.
 *
 * Three things go, in the order that matters if the tab dies halfway:
 *
 *  1. **The manifest rows first.** They are what `/offline` renders; a book that is not listed is a
 *     book nobody can tap, even if its bytes are briefly still on disk. Orphaned bytes are already
 *     handled — `evictOrphanAudio()` on the offline screen reclaims them.
 *  2. **Their audio**, through `removeOfflineBook`, which already knows not to delete a URL another
 *     retained download still wants (two copies of the same public-domain text share an artifact).
 *  3. **`ab-book-v1`**, the whole cached-text cache, by message to the worker. Not filtered by book:
 *     the cache is keyed by request URL and the worker cannot be asked "which of these was private"
 *     without a second index that would then be the thing that goes stale.
 *
 * Public books are kept on purpose — see `OfflineBook.visibility`. Deleting a stranger's downloaded
 * copy of *Alice* to protect a book anybody can open signed-out protects nothing and costs a 40 MB
 * re-download on a phone.
 *
 * Never throws. A sign-out that fails because a browser refused IndexedDB must still sign the person
 * out; the failure to report here is a stale download, and the failure to prevent is being unable to
 * leave.
 */
export async function clearPrivateOfflineData(): Promise<{ removed: string[]; freed: number }> {
  const removed: string[] = [];
  let freed = 0;
  try {
    for (const book of await listOfflineBooks()) {
      if (book.visibility === "public") continue;
      freed += await removeOfflineBook(book.bookId);
      removed.push(book.bookId);
    }
  } catch {
    /* A browser that refuses IndexedDB still has to be able to sign out. */
  }
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: "ab-clear-book-cache" });
  } catch {
    /* No worker, or no controller yet. Nothing was cached by one either, then. */
  }
  return { removed, freed };
}

// ─── ab-audio-v1: the page's own cache (LAW 14) ──────────────────────────────

/**
 * ⚠️ **This name is also in `client/src/sw.ts`, and it must stay identical.**
 *
 * It cannot be imported from there: `sw.ts` registers service-worker event listeners at module
 * scope, so importing it from the app would install a service worker's handlers inside a page. The
 * service worker names it in exactly one place — the `activate` sweep, so that a deploy never
 * deletes a reader's downloaded audio — and `test/sw.test.ts` asserts the two spellings agree, which
 * is the same discipline `test/wiring.test.ts` uses everywhere an import is impossible.
 */
export const AUDIO_CACHE = "ab-audio-v1";

export async function openAudioCache(): Promise<Cache | null> {
  if (typeof caches === "undefined") return null;
  try {
    return await caches.open(AUDIO_CACHE);
  } catch {
    // Cache API is unavailable on insecure origins and in some private modes. Downloads are simply
    // not offered; nothing else in the product changes.
    return null;
  }
}

/**
 * The offline half of §5.7's source strategy, and the reason LAW 14 costs nothing.
 *
 * The audio engine's host `fetch` calls this when the network refuses. A hit is a real `Response`
 * carrying the whole artifact, which `PageWindow` turns into a Blob and an `objectURL` — the exact
 * path §5.7 already specifies for "prefetched pages and offline books", now reached from local
 * storage instead of from the wire. No Range is involved, so none of LAW 14's failure modes are.
 */
export async function cachedAudioResponse(url: string): Promise<Response | null> {
  const cache = await openAudioCache();
  if (!cache) return null;
  try {
    return (await cache.match(url)) ?? null;
  } catch {
    return null;
  }
}

/**
 * The worker's book-content cache. Same rule as `AUDIO_CACHE`: spelled in `sw.ts` too, and
 * `test/sw.test.ts` holds the two spellings together.
 */
export const BOOK_CACHE = "ab-book-v1";

/**
 * A narration plan the service worker stored, read straight from `CacheStorage`.
 *
 * Read from the page rather than through a `fetch`, because the worker's strategy is network FIRST:
 * in a dead zone its `fetch` hangs exactly as long as ours does, which is the wait this exists to
 * skip for a book the listener saved for offline.
 */
export async function cachedBookResponse(url: string): Promise<Response | null> {
  if (typeof caches === "undefined") return null;
  try {
    return (await caches.match(url, { cacheName: BOOK_CACHE })) ?? null;
  } catch {
    return null;
  }
}

/**
 * Delete every cached artifact no downloaded book still names.
 *
 * This is the whole of the audio eviction policy, and the omission is deliberate: there is **no
 * silent byte-capped LRU**. Evicting a book somebody explicitly downloaded, in the background,
 * because a different book was downloaded later, produces a phone that goes quiet in a tunnel with
 * no explanation. Storage pressure is answered by telling the reader the number before the download
 * and by letting them remove a book, both on `pages/Offline.tsx`. What this reclaims is only the
 * genuinely unreferenced: a download that failed halfway, or a book whose manifest row was lost.
 */
export async function evictOrphanAudio(): Promise<{ deleted: number }> {
  const cache = await openAudioCache();
  if (!cache) return { deleted: 0 };
  const wanted = new Set<string>();
  for (const book of await listOfflineBooks()) {
    for (const page of book.pages) wanted.add(new URL(page.url, location.origin).href);
  }
  let deleted = 0;
  for (const req of await cache.keys()) {
    if (wanted.has(req.url)) continue;
    if (await cache.delete(req)) deleted++;
  }
  return { deleted };
}

// ─── the read-through hook ───────────────────────────────────────────────────

export interface CachedQuery<T> {
  /** The value to render. Cached first, replaced by fresh the moment it lands. */
  data: T | null;
  /** True while the *first* answer (cache or network) is still outstanding. */
  loading: boolean;
  /** True while a network fetch is in flight over already-painted data. */
  revalidating: boolean;
  /** The network's refusal, if any. Non-null with `data` non-null means "showing you the old one". */
  error: Error | null;
  /** When `data` came off disk rather than the wire. */
  stale: boolean;
  reload: () => void;
}

/**
 * Paint the last answer, then go and get a new one.
 *
 * `key` is the cache key **and** the dependency: change it (a different scope, a different book) and
 * the hook re-runs. The network answer always wins; a failure leaves the cached copy on screen with
 * `error` set, so the screen can say "this is what we had, and here is why it's not newer" rather
 * than blanking a shelf somebody was reading.
 */
export function useCachedQuery<T>(key: string, load: () => Promise<T>): CachedQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [revalidating, setRevalidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [stale, setStale] = useState(false);
  const [nonce, setNonce] = useState(0);

  // `load` is usually an inline closure and would otherwise re-run the effect on every render.
  const loadRef = useRef(load);
  loadRef.current = load;
  const keyRef = useRef(key);

  useEffect(() => {
    if (keyRef.current !== key) {
      // A different question is being asked. Drop the previous answer rather than showing last
      // month's shelf under this month's heading; `reload()` deliberately does NOT do this, because
      // re-fetching the same key should refresh under the reader, not blank the screen.
      keyRef.current = key;
      setData(null);
      setStale(false);
      setLoading(true);
    }
    let live = true;
    // Set inside the effect, not read from render state: the disk read and the network read race,
    // and the only thing that matters is which of them answered first *for this run*.
    let answered = false;
    setRevalidating(true);
    setError(null);

    void idbGet<T>(key).then((hit) => {
      // Only paint the cache if the network hasn't already answered — otherwise a slow disk read
      // overwrites fresh data with old, which is the one thing a cache must never do.
      if (!live || answered || !hit) return;
      setData(hit.value);
      setStale(true);
      setLoading(false);
    });

    void loadRef
      .current()
      .then((fresh) => {
        if (!live) return;
        answered = true;
        setData(fresh);
        setStale(false);
        setError(null);
        void idbSet(key, fresh);
      })
      .catch((err: unknown) => {
        if (!live) return;
        answered = true;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!live) return;
        setLoading(false);
        setRevalidating(false);
      });

    return () => {
      live = false;
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, revalidating, error, stale, reload };
}
